import type { ProjectResponse } from '@palaccount/contracts';
import { MAX_PICKER_IDS, PICKER_PAGE_SIZE, pickerIds } from '@palaccount/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '@/api';
import type { TypeaheadOption } from '@/components/ui/typeahead';
import { throwApiError } from '@/lib/server-messages';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';

import { PROJECTS_KEY } from './api';

/**
 * THE PROJECT PICKER (GEN-50) — PRJ-02's board and PRJ-04's billing screen,
 * which both resolved a project from a `<select>` filled by `useProjects()`.
 *
 * That hook read EVERY project and PRICED every one of them: `toResponse`
 * runs the consumption engine per row. Two bounded reads replace it, and the
 * held one matters here for a reason BR-PRJ-01-09 states outright — a
 * called-off project is CANCELLED rather than deleted and its approved hours
 * stay billable, so the billing screen must still be able to name it.
 */
export function useProjectSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'search', q],
    enabled,
    queryFn: async (): Promise<ProjectResponse[]> => {
      const response = await api.projects.list({
        query: { ...(q === '' ? {} : { q }), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.projects;
    },
    placeholderData: keepPreviousData,
  });
}

/** The projects a screen already holds, by id and by nothing else. */
export function useProjectsHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<ProjectResponse[]> => {
      const response = await api.projects.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.projects;
    },
  });
}

/**
 * The project a screen opens on when none is chosen — ONE row, asked for as
 * one row.
 *
 * PRJ-02 and PRJ-04 both took `projects[0]?.id` off a whole-catalogue read.
 * "First" was always the server's ORDER BY rather than the client's, so a
 * page of one gives the same answer for a fraction of the work — the same
 * move `useFirstBom()` makes on MFG-01.
 */
export function useFirstProject() {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'first'],
    queryFn: async (): Promise<ProjectResponse | null> => {
      const response = await api.projects.list({ query: { page: 1, pageSize: 1 } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.projects[0] ?? null;
    },
  });
}

/**
 * `enabled` is for a field that is only SOMETIMES a project field — INV-08's
 * voucher offers one on a Stock OUT and none on a Stock IN — so an inbound
 * voucher does not ask the server for projects it cannot name.
 */
export function useProjectPicker(
  heldId: string | null | undefined,
  enabled = true,
): PickerState<ProjectResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);

  const matches = useProjectSearch(search, enabled);
  const held = useProjectsHeld(ids);

  return usePickerState<ProjectResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: projectOption,
    loading: matches.isFetching,
  });
}

/** PRJ-01 §4's own two columns: the project, and the code beside it. */
export function projectOption(project: ProjectResponse): TypeaheadOption {
  return {
    id: project.id,
    label: project.name,
    hint: project.code,
    inactive: project.status === 'cancelled' || project.status === 'completed',
  };
}
